/**
 * API Client für Versionen
 * 
 * Endpoints für Versionierung und Rollback.
 */

import apiClient from '../api-client'
import type {
  VersionsResponse,
  VersionResponse,
  RollbackResponse,
  DiffResponse,
  CreateVersionRequest,
} from '@/types/versioning'

// =============================================================================
// CONTENT BLOCK VERSIONS
// =============================================================================

const CONTENT_BLOCKS_URL = '/api/v1/content-blocks'

/**
 * Get all versions of a content block
 */
export async function getContentBlockVersions(
  blockId: string
): Promise<VersionsResponse> {
  const response = await apiClient.get<VersionsResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions`
  )
  return response.data
}

/**
 * Get a specific version of a content block
 */
export async function getContentBlockVersion(
  blockId: string,
  versionId: string
): Promise<VersionResponse> {
  const response = await apiClient.get<VersionResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions/${versionId}`
  )
  return response.data
}

/**
 * Create a new version of a content block
 */
export async function createContentBlockVersion(
  blockId: string,
  data: CreateVersionRequest
): Promise<VersionResponse> {
  const response = await apiClient.post<VersionResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions`,
    data
  )
  return response.data
}

/**
 * Rollback to a specific version
 */
export async function rollbackContentBlockVersion(
  blockId: string,
  versionId: string
): Promise<RollbackResponse> {
  const response = await apiClient.post<RollbackResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions/${versionId}/rollback`
  )
  return response.data
}

/**
 * Manually publish a version
 */
export async function publishContentBlockVersion(
  blockId: string,
  versionId: string
): Promise<VersionResponse> {
  const response = await apiClient.post<VersionResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions/${versionId}/publish`
  )
  return response.data
}

/**
 * Get diff between two versions
 */
export async function getContentBlockVersionDiff(
  blockId: string,
  versionAId: string,
  versionBId: string
): Promise<DiffResponse> {
  const response = await apiClient.get<DiffResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/versions/${versionAId}/diff/${versionBId}`
  )
  return response.data
}

// =============================================================================
// CHANNEL PACKAGE VERSIONS
// =============================================================================

const CHANNEL_PACKAGES_URL = '/api/v1/channels'

/**
 * Get all versions of a channel package
 */
export async function getChannelPackageVersions(
  channelId: string,
  packageId: string
): Promise<VersionsResponse> {
  const response = await apiClient.get<VersionsResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/versions`
  )
  return response.data
}

/**
 * Rollback channel package to a specific version
 */
export async function rollbackChannelPackageVersion(
  channelId: string,
  packageId: string,
  versionId: string
): Promise<RollbackResponse> {
  const response = await apiClient.post<RollbackResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/versions/${versionId}/rollback`
  )
  return response.data
}

/**
 * Manually publish a channel package version
 */
export async function publishChannelPackageVersion(
  channelId: string,
  packageId: string,
  versionId: string
): Promise<VersionResponse> {
  const response = await apiClient.post<VersionResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/versions/${versionId}/publish`
  )
  return response.data
}

/**
 * Get diff between two channel package versions
 */
export async function getChannelPackageVersionDiff(
  channelId: string,
  packageId: string,
  versionAId: string,
  versionBId: string
): Promise<DiffResponse> {
  const response = await apiClient.get<DiffResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/versions/${versionAId}/diff/${versionBId}`
  )
  return response.data
}


